import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { useSummonAdminMutation } from 'store/services/case/caseApi';
import { messageApi } from 'store/services/message/messageApi';
import { AssetUrls } from 'config/assets';
import type { CaseReason, CaseSource } from 'types/interfaces/thread';
import type { SummonAdminResponse } from 'types/interfaces/case';
import { CANCEL_NOTE, caseReasonLongLabel, reasonsForRole } from 'constants/caseReasons';
import { ACCEPTED_IMAGE_ACCEPT_ATTR, validateImageFiles } from 'constants/messageImages';
import { formatCaseNumber } from 'utils/caseNumber';
import type { ContactTradeSummary } from 'components/ContactUserModal/contactTradeSummary';
import {
  DESCRIPTION_MAX,
  PHOTOS_MAX,
  WizardStep,
  daysSince,
  initialAffectedItemIds,
  isStepComplete,
  roleWord,
  stepTwoRequirements,
} from './wizardRules';
import 'styles/caseSummoned.css';
import './SummonAdminWizard.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  trade: ContactTradeSummary;
  source: CaseSource;
  /** Called with the backend's case after a successful summon (the caller refetches its case state). */
  onSummoned?: (response: SummonAdminResponse) => void;
}

const STEP_LABELS: Record<WizardStep, string> = { 1: 'Reason', 2: 'Details', 3: 'Review' };

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return 'Not yet';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Summon Admin wizard (Asana 1219952262403623, Decisions 16 to 18): reason,
 * details, review, then a success state. Reasons depend on the caller's role;
 * the gates live in wizardRules.ts. Submitting on a trade whose case is
 * resolved reopens it server-side; on a partner's open case the backend adds
 * the caller and returns that case.
 */
export const SummonAdminWizard: React.FC<Props> = ({ isOpen, onClose, trade, source, onSummoned }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [summonAdmin, { isLoading }] = useSummonAdminMutation();

  const [step, setStep] = useState<WizardStep>(1);
  const [reason, setReason] = useState<CaseReason | null>(null);
  const [affectedItemIds, setAffectedItemIds] = useState<string[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [result, setResult] = useState<SummonAdminResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reasons = useMemo(() => reasonsForRole(trade.role), [trade.role]);
  const itemsWithIds = useMemo(() => trade.items.filter((i): i is typeof i & { id: string } => !!i.id), [trade.items]);
  const allItemIds = useMemo(() => itemsWithIds.map((i) => i.id), [itemsWithIds]);

  // Reset when closed so a reopen starts clean.
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setReason(null);
      setAffectedItemIds([]);
      photoUrls.forEach((u) => URL.revokeObjectURL(u));
      setPhotos([]);
      setPhotoUrls([]);
      setDescription('');
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const chooseReason = (next: CaseReason) => {
    setReason(next);
    setAffectedItemIds(initialAffectedItemIds(next, allItemIds));
    if (!stepTwoRequirements(next).showPhotos) {
      photoUrls.forEach((u) => URL.revokeObjectURL(u));
      setPhotos([]);
      setPhotoUrls([]);
    }
  };

  const addPhotos = useCallback((files: File[]) => {
    const remaining = PHOTOS_MAX - photos.length;
    if (remaining <= 0) {
      toast.warning(`Maximum ${PHOTOS_MAX} photos`);
      return;
    }
    const { accepted, errors, capHit } = validateImageFiles(files, remaining);
    errors.forEach((e) => toast.error(e));
    if (capHit) toast.warning(`Only ${remaining} more photo(s) can be added`);
    if (accepted.length > 0) {
      setPhotos((prev) => [...prev, ...accepted]);
      setPhotoUrls((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
    }
  }, [photos.length]);

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photoUrls[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleItem = (id: string) =>
    setAffectedItemIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const state = { reason, affectedItemIds, photoCount: photos.length, description };
  const canContinue = isStepComplete(step, state);
  const requirements = reason ? stepTwoRequirements(reason) : null;

  const submit = async () => {
    if (!reason || !canContinue) return;
    try {
      const response = await summonAdmin({
        tradeId: trade.tradeId,
        reason,
        affectedItemIds: requirements?.showCards ? affectedItemIds : [],
        images: requirements?.showPhotos ? photos : [],
        description: description.trim() || undefined,
        source,
      }).unwrap();
      setResult(response);
      // The summon card now sits in the trade thread for both parties, the
      // row's chip reads Case open and the trade's caseSummary changed:
      // refetch the list, the counts and any open thread.
      dispatch(messageApi.util.invalidateTags(['Messages', 'InboxCounts']));
      onSummoned?.(response);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to summon admin. Please try again.');
    }
  };

  if (!isOpen) return null;

  const partner = trade.partnerUsername;
  const caseNumber = formatCaseNumber(result?.caseNumber);
  const affectedNames = itemsWithIds.filter((i) => affectedItemIds.includes(i.id)).map((i) => i.name);

  const content = (
    <div className="summon-wizard-overlay" onClick={onClose}>
      <div className="summon-wizard" role="dialog" aria-modal="true" aria-label="Get help with this trade" onClick={(e) => e.stopPropagation()}>
        <div className="summon-wizard-header">
          <div>
            <h2 className="summon-wizard-title">{result ? 'Admin summoned' : 'Get help with this trade'}</h2>
            <p className="summon-wizard-subtitle">Trade {trade.tradeNumber} · {roleWord(trade.role)} {partner}</p>
          </div>
          <button type="button" className="summon-wizard-close" onClick={onClose} aria-label="Close">
            <img src={AssetUrls.icons.dropdowns.close} alt="" style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {!result && (
          <ol className="summon-wizard-progress" aria-label="Steps">
            {([1, 2, 3] as WizardStep[]).map((s) => (
              <li key={s} className={`summon-wizard-progress-step${s < step ? ' done' : ''}${s === step ? ' current' : ''}`} aria-current={s === step ? 'step' : undefined}>
                <span className="summon-wizard-progress-bar" />
                <span className="summon-wizard-progress-label">{s} {STEP_LABELS[s]}</span>
              </li>
            ))}
          </ol>
        )}

        <div className="summon-wizard-body">
          {result ? (
            <div className="summon-wizard-success" data-testid="wizard-success">
              <p className="summon-wizard-success-text">
                {caseNumber ? `Case ${caseNumber} is open` : 'A case is open'}. You and {partner} will get updates in this trade's thread.
              </p>
            </div>
          ) : step === 1 ? (
            <div className="summon-wizard-reasons" role="radiogroup" aria-label="Reason">
              {reasons.map((option) => (
                <label key={option.value} className={`summon-wizard-reason${reason === option.value ? ' selected' : ''}`}>
                  <input type="radio" name="summon-reason" value={option.value} checked={reason === option.value} onChange={() => chooseReason(option.value)} />
                  <span>
                    <span className="summon-wizard-reason-label">{option.label}</span>
                    <span className="summon-wizard-reason-helper">{option.helper}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : step === 2 && reason && requirements ? (
            <div className="summon-wizard-details">
              {requirements.showShipFacts && (
                <dl className="summon-wizard-facts">
                  <div><dt>Shipped</dt><dd>{formatDate(trade.shippedAt)}</dd></div>
                  {trade.trackingNumber && <div><dt>Tracking</dt><dd>{trade.trackingNumber}</dd></div>}
                  <div><dt>Est. arrival</dt><dd>{trade.estimatedArrivalLabel || 'No estimate'}</dd></div>
                  <div><dt>Days since ship</dt><dd>{daysSince(trade.shippedAt) ?? 'Not shipped'}</dd></div>
                </dl>
              )}

              {requirements.showCards && (
                <fieldset className="summon-wizard-cards">
                  <legend>Affected cards {requirements.cardsRequired && <span className="summon-wizard-required">(at least 1)</span>}</legend>
                  {itemsWithIds.length === 0 ? (
                    <p className="summon-wizard-hint">This trade's cards could not be listed. Describe the affected cards below.</p>
                  ) : (
                    itemsWithIds.map((item) => (
                      <label key={item.id} className="summon-wizard-card">
                        <input type="checkbox" checked={affectedItemIds.includes(item.id)} onChange={() => toggleItem(item.id)} />
                        <span>{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}</span>
                      </label>
                    ))
                  )}
                </fieldset>
              )}

              {requirements.showPhotos && (
                <div className="summon-wizard-photos">
                  <div className="summon-wizard-photos-label">
                    Photos of what arrived <span className="summon-wizard-optional">· optional, up to {PHOTOS_MAX}</span>
                  </div>
                  <div className="summon-wizard-photo-strip">
                    {photoUrls.map((url, i) => (
                      <div key={url} className="summon-wizard-photo">
                        <img src={url} alt={`Attachment ${i + 1}`} />
                        <button type="button" onClick={() => removePhoto(i)} aria-label={`Remove photo ${i + 1}`}>×</button>
                      </div>
                    ))}
                    {photos.length < PHOTOS_MAX && (
                      <button type="button" className="summon-wizard-photo-add" onClick={() => fileInputRef.current?.click()}>
                        + Add photo
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_IMAGE_ACCEPT_ATTR}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => { addPhotos(Array.from(e.target.files || [])); e.target.value = ''; }}
                  />
                </div>
              )}

              {requirements.showCancelNote && (
                <p className="summon-wizard-note">{CANCEL_NOTE[trade.role]}</p>
              )}

              <div className="summon-wizard-description">
                <label className="summon-wizard-label" htmlFor="summon-description">
                  Details <span className="summon-wizard-optional">(optional)</span>
                </label>
                <textarea
                  id="summon-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={DESCRIPTION_MAX}
                  rows={4}
                  placeholder="Anything an admin should know"
                />
                <CharacterCounter currentLength={description.length} maxLength={DESCRIPTION_MAX} warningThreshold={1800} />
                <p className="summon-wizard-visibility">
                  Everything you add here, including photos, is visible to {partner} and TradeMagic admins.
                </p>
              </div>
            </div>
          ) : (
            <div className="summon-wizard-review">
              <dl className="summon-wizard-facts">
                <div><dt>Trade</dt><dd>{trade.tradeNumber}</dd></div>
                <div><dt>Reason</dt><dd>{reason ? caseReasonLongLabel(reason) : ''}</dd></div>
                {requirements?.showCards && <div><dt>Affected cards</dt><dd>{affectedNames.join(', ') || 'None'}</dd></div>}
                {requirements?.showPhotos && <div><dt>Photos</dt><dd>{photos.length > 0 ? photos.length : 'None added'}</dd></div>}
                <div><dt>Details</dt><dd>{description.trim() || 'None added'}</dd></div>
              </dl>
              <div className="case-summoned-status">
                <img src={AssetUrls.icons.navigation.adminCases} alt="" style={{ width: 20, height: 20 }} />
                <p className="summoned-details" style={{ margin: 0 }}>
                  This adds a case to Trade {trade.tradeNumber}'s thread. {partner} will see your reason and photos, and admins can read the whole thread.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="summon-wizard-footer">
          {result ? (
            // Started from the thread (INBOX): the reader is already there, so
            // Close is the only button; elsewhere a second one goes to the thread.
            source === 'INBOX' ? (
              <MagicButton label="Close" color="purple" onPress={onClose} />
            ) : (
              <>
                <MagicButton label="Close" color="dark-grey" onPress={onClose} />
                <MagicButton
                  label="Back to trade thread"
                  color="purple"
                  onPress={() => { onClose(); navigate(`/inbox/${result.conversationId}`); }}
                />
              </>
            )
          ) : (
            <>
              {step > 1 ? (
                <MagicButton label="Back" color="dark-grey" onPress={() => setStep((s) => (s - 1) as WizardStep)} />
              ) : (
                <MagicButton label="Cancel" color="dark-grey" onPress={onClose} />
              )}
              <div className={`summon-wizard-primary${canContinue ? '' : ' blocked'}`}>
                {step < 3 ? (
                  <MagicButton label="Continue" color="purple" onPress={() => canContinue && setStep((s) => (s + 1) as WizardStep)} isDisabled={!canContinue} />
                ) : (
                  <MagicButton label={isLoading ? 'Summoning…' : 'Summon Admin'} color="black" onPress={submit} isDisabled={!canContinue || isLoading} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SummonAdminWizard;
